import { Checkbox, FormControlLabel } from "@material-ui/core";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  MoreHoriz as MoreIcon,
} from "@material-ui/icons";
import {
  ActionList,
  Banner,
  Button,
  ButtonGroup,
  Card,
  DataTable,
  FormLayout,
  Layout,
  Modal,
  Popover,
  Select,
  Spinner,
  TextContainer,
  TextField,
} from "@shopify/polaris";
import { useAdmin } from "context/admin";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ModalEditWindow from "./ModalEditWindow";

export default function CalendarSettings() {
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingWindow, setEditingWindow] = useState(false);
  const [calendarPopup, setCalendarPopup] = useState(false);
  const [form, setForm] = useState({});
  const [activeType, setActiveType] = useState("pickup");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const admin = useAdmin();

  const shopConfig = admin.selectedShop.shop_config || {};
  const [syncWindows, setSyncWindows] = useState(
    shopConfig.sync_calendar_windows !== false
  );

  // Get shop locations and location_settings
  const locations = admin.selectedShop?.locations || [];
  const locationSettings = shopConfig.location_settings || {};

  // Build location options for the selector
  const locationOptions = useMemo(() => {
    const options = [{ label: "All Locations (Shop Default)", value: "" }];
    locations
      .filter((loc) => loc.active)
      .forEach((loc) => {
        options.push({
          label: loc.name,
          value: loc.id.toString(),
        });
      });
    return options;
  }, [locations]);

  // Get location-specific calendar if exists
  const getLocationCalendar = useCallback(
    (locId) => {
      if (!locId) return null;
      const locConfig = locationSettings[locId] || {};
      return locConfig.calendar;
    },
    [locationSettings]
  );

  // Check if location has custom calendar
  const locationHasCustomCalendar = useMemo(() => {
    if (!selectedLocationId) return false;
    const locConfig = locationSettings[selectedLocationId] || {};
    return !!locConfig.calendar;
  }, [selectedLocationId, locationSettings]);

  // Handle clearing location-specific calendar (revert to shop default)
  const handleClearLocationCalendar = () => {
    if (!selectedLocationId) return;

    const locConfig = { ...(locationSettings[selectedLocationId] || {}) };
    delete locConfig.calendar;

    admin.updateShopConfig({
      location_settings: {
        ...locationSettings,
        [selectedLocationId]: locConfig,
      },
    });
  };

  // TODO: Get courier from shop's carrier
  const WINDOWS = {
    default: [
      {
        id: "MO",
        name: "10am - 2pm",
        limit: 0,
        active: true,
        collection_time: 10,
        due_time: 14,
      },
      {
        id: "AF",
        name: "2pm - 5pm",
        limit: 0,
        active: true,
        collection_time: 14,
        due_time: 17,
      },
    ],
    SAMEDAY: [
      {
        id: "AF",
        name: "12pm - 7pm",
        limit: 0,
        active: true,
        collection_time: 12,
        due_time: 19,
      },
    ],
    BoxKnight: [
      {
        id: "AF",
        name: "4pm - 7pm",
        limit: 0,
        active: true,
        collection_time: 16,
        due_time: 19,
      },
    ],
    METROSPEEDY: [
      {
        id: "AF",
        name: "12pm - 4pm",
        limit: 0,
        active: true,
        collection_time: 12,
        due_time: 16,
      },
    ],
  };

  const calendar = admin.selectedShop.shop_config
    ? admin.selectedShop.shop_config.calendar
    : {};
  const defaultWindows = WINDOWS[admin.selectedShop.carrier] || WINDOWS.default;

  // Normalize legacy calendar format to new format with pickup/delivery separation
  const normalizeCalendar = (cal) => {
    const normalized = {};
    Object.keys(cal || {}).forEach((date) => {
      const dayData = cal[date];
      // Check if it's already in new format (has pickup or delivery keys)
      if (dayData.pickup || dayData.delivery) {
        normalized[date] = dayData;
      } else {
        // Legacy format - convert to new format
        normalized[date] = {
          pickup: { ...dayData },
          delivery: { ...dayData },
        };
      }
    });
    return normalized;
  };

  const normalizedCalendar = useMemo(() => normalizeCalendar(calendar), [
    calendar,
  ]);

  // Get the effective calendar (location-specific or shop default)
  const effectiveCalendar = useMemo(() => {
    if (selectedLocationId) {
      const locCalendar = getLocationCalendar(selectedLocationId);
      if (locCalendar) {
        return normalizeCalendar(locCalendar);
      }
    }
    return normalizedCalendar;
  }, [selectedLocationId, getLocationCalendar, normalizedCalendar]);

  useEffect(() => {
    if (!saving) {
      setForm(effectiveCalendar);
    }
  }, [effectiveCalendar, saving]);

  useEffect(() => {
    setSyncWindows(shopConfig.sync_calendar_windows !== false);
  }, [shopConfig.sync_calendar_windows]);

  const handleSyncToggle = (checked) => {
    setSyncWindows(checked);
    admin.updateShopConfig({ sync_calendar_windows: checked });
  };

  const handleAddDate = () => {
    const baseDay = {
      closed: false,
      windows: defaultWindows,
    };
    const newDay = {
      date: "",
      day: {
        pickup: { ...baseDay },
        delivery: { ...baseDay },
      },
    };
    setEditing(newDay);
  };

  const handleChange = (key, val) => {
    setEditing({
      ...editing,
      [key]: val,
    });
  };

  const handleSave = (calendarData) => {
    setSaving(true);

    if (selectedLocationId) {
      // Save to location-specific settings
      const promise = admin.updateShopConfig({
        location_settings: {
          ...locationSettings,
          [selectedLocationId]: {
            ...(locationSettings[selectedLocationId] || {}),
            calendar: calendarData,
          },
        },
      });

      promise.finally(() => {
        setSaving(false);
        setEditing(false);
        setCalendarPopup(false);
        setEditingWindow(false);
      });
    } else {
      // Save to shop default
      admin.saveCalendar(calendarData).finally(() => {
        setSaving(false);
        setEditing(false);
        setCalendarPopup(false);
        setEditingWindow(false);
      });
    }
  };

  const handleSaveWithType = (date, dayData, type) => {
    const updatedCalendar = {
      ...form,
      [date]: {
        ...form[date],
        [type]: dayData,
      },
    };

    // If sync is enabled, apply changes to both pickup and delivery
    if (syncWindows) {
      updatedCalendar[date] = {
        pickup: dayData,
        delivery: dayData,
      };
    }

    handleSave(updatedCalendar);
  };

  const handleRemove = (date) => {
    setSaving(true);
    const updatedCalendar = { ...form };
    delete updatedCalendar[date];
    admin.saveCalendar(updatedCalendar).finally(() => setSaving(false));
  };

  const saveDisabled = !editing || !editing.date;

  const headers = [
    {
      type: "text",
      label: "Open",
      fn: ({ date, day }) => (
        <Checkbox
          disabled={saving}
          checked={day.closed}
          tabIndex={-1}
          checkedIcon={<CloseIcon />}
          icon={<CheckIcon />}
          onClick={() =>
            handleSaveWithType(
              date,
              { ...day, closed: !day.closed },
              activeType
            )
          }
        />
      ),
    },
    { type: "text", label: "Date", fn: ({ date }) => date },
    {
      type: "text",
      label: "Windows",
      fn: ({ date, day }) =>
        day.closed ? (
          "Shop is closed."
        ) : (
          <div>
            {day.windows.map((window) => (
              <span key={window.id}>
                <Checkbox
                  disabled={saving}
                  checked={window.active}
                  onChange={() =>
                    handleSaveWithType(
                      date,
                      {
                        ...day,
                        windows: day.windows.map((w) =>
                          w.id === window.id
                            ? { ...window, active: !window.active }
                            : w
                        ),
                      },
                      activeType
                    )
                  }
                  name={window.id}
                  color="primary"
                />
                <span
                  onClick={() =>
                    setEditingWindow({ date, day, window, type: activeType })
                  }
                >
                  {" " + window.name}
                </span>
              </span>
            ))}
          </div>
        ),
    },
    {
      type: "text",
      label: "Limit Per Window",
      fn: ({ date, day }) => {
        const currentLimit = day.windows?.[0]?.limit ?? 0;
        return (
          <TextField
            type="text"
            min={0}
            value={`${currentLimit}`}
            onBlur={() => {
              handleSave(form);
            }}
            onChange={(val) => {
              if (!isNaN(val)) {
                const updatedDay = {
                  ...day,
                  windows: day.windows.map((w) => ({ ...w, limit: +val })),
                };

                // If sync is enabled, apply to both pickup and delivery
                if (syncWindows) {
                  setForm({
                    ...form,
                    [date]: {
                      pickup: updatedDay,
                      delivery: updatedDay,
                    },
                  });
                } else {
                  setForm({
                    ...form,
                    [date]: {
                      ...form[date],
                      [activeType]: updatedDay,
                    },
                  });
                }
              }
            }}
          />
        );
      },
    },
    {
      type: "numeric",
      col: null,
      label: "",
      fn: ({ date }) => {
        return (
          <Popover
            active={calendarPopup === date}
            activator={
              saving ? (
                <Spinner accessibilityLabel="Saving" size="small" />
              ) : (
                <MoreIcon
                  style={{ cursor: "pointer" }}
                  onClick={() => setCalendarPopup(date)}
                />
              )
            }
            onClose={() => setCalendarPopup(false)}
          >
            <ActionList
              items={[
                {
                  content: "Delete",
                  onAction: () => handleRemove(date),
                  destructive: true,
                },
              ]}
            />
          </Popover>
        );
      },
    },
  ];

  return (
    <Layout.AnnotatedSection
      title="Calendar"
      description="Override weekly schedule on specific dates for pickup and delivery."
    >
      <Card>
        <Card.Header
          actions={[{ content: "Add", onAction: handleAddDate }]}
          title="Calendar Settings"
        ></Card.Header>
        <Card.Section>
          <div
            style={{
              marginBottom: "1rem",
              display: "flex",
              gap: "1rem",
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <ButtonGroup segmented>
              <Button
                pressed={activeType === "pickup"}
                onClick={() => setActiveType("pickup")}
              >
                Pickup Calendar
              </Button>
              <Button
                pressed={activeType === "delivery"}
                onClick={() => setActiveType("delivery")}
              >
                Delivery Calendar
              </Button>
            </ButtonGroup>
            {locations.length > 1 && (
              <div style={{ minWidth: "200px" }}>
                <Select
                  label="Location"
                  labelInline
                  options={locationOptions}
                  value={selectedLocationId}
                  onChange={setSelectedLocationId}
                />
              </div>
            )}
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={syncWindows}
                  onChange={(e) => handleSyncToggle(e.target.checked)}
                  color="primary"
                />
              }
              label="Sync pickup and delivery windows (changes apply to both)"
            />
          </div>
        </Card.Section>
        {selectedLocationId && (
          <Card.Section>
            <Banner
              status={locationHasCustomCalendar ? "warning" : "info"}
              action={
                locationHasCustomCalendar
                  ? {
                      content: "Reset to Shop Default",
                      onAction: handleClearLocationCalendar,
                    }
                  : undefined
              }
            >
              <p>
                <strong>
                  {locationHasCustomCalendar
                    ? "Editing custom calendar for"
                    : "Creating custom calendar for"}
                </strong>{" "}
                <strong>
                  {locations.find((l) => l.id.toString() === selectedLocationId)
                    ?.name || "this location"}
                </strong>
                .{" "}
                {locationHasCustomCalendar
                  ? "This location has its own calendar overrides."
                  : "Changes will create a location-specific calendar. Orders assigned to this location will use these date overrides."}
              </p>
            </Banner>
          </Card.Section>
        )}
        {Object.keys(form).length > 0 ? (
          <DataTable
            columnContentTypes={headers.map((h) => h.type)}
            headings={headers.map((h) => h.label)}
            rows={Object.keys(form).map((date) => {
              const dayData =
                form[date] && form[date][activeType]
                  ? form[date][activeType]
                  : { closed: false, windows: [] };
              return headers.map((header) => header.fn({ date, day: dayData }));
            })}
          />
        ) : (
          <Card.Section>
            <TextContainer>
              Close on certain dates, override order limits and open different
              pickup and delivery windows.
            </TextContainer>
          </Card.Section>
        )}
      </Card>

      <Modal
        title="Add Date"
        open={editing}
        onClose={() => setEditing(false)}
        secondaryActions={[
          { content: "Cancel", onAction: () => setEditing(false) },
        ]}
        primaryAction={{
          content: "Save",
          loading: saving,
          onAction: () => {
            const newCalendar = { ...form, [editing.date]: editing.day };
            handleSave(newCalendar);
          },
          disabled: saving || saveDisabled,
        }}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Date"
              type="date"
              min={0}
              value={`${editing.date}`}
              onChange={(val) => handleChange("date", val)}
            />
            {syncWindows && (
              <TextContainer>
                <p style={{ color: "#637381", fontSize: "0.9rem" }}>
                  Note: Sync is enabled. This date will have the same windows
                  for pickup and delivery.
                </p>
              </TextContainer>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>

      <ModalEditWindow
        open={!!editingWindow}
        editingWindow={editingWindow}
        setEditingWindow={setEditingWindow}
        saving={saving}
        onClose={() => setEditingWindow(false)}
        onSave={() => {
          const updatedDay = {
            ...editingWindow.day,
            windows: editingWindow.day.windows.map((w) =>
              w.id === editingWindow.window.id ? editingWindow.window : w
            ),
          };

          const updatedCalendar = {
            ...form,
            [editingWindow.date]: {
              ...form[editingWindow.date],
              [editingWindow.type || activeType]: updatedDay,
            },
          };

          // If sync is enabled, apply to both
          if (syncWindows) {
            updatedCalendar[editingWindow.date] = {
              pickup: updatedDay,
              delivery: updatedDay,
            };
          }

          handleSave(updatedCalendar);
        }}
      />
    </Layout.AnnotatedSection>
  );
}
